import React, { useState } from "react";
import {
  MyRegistrationItem,
  MyRegistrationEnrollee,
} from "../../api/getMyRegistrations/types";
import axiosInstance from "@/api/axiosInstance";
import { toast } from "@/hooks/useToast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { CalendarDays, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

interface MyRegistrationCardProps {
  registration: MyRegistrationItem;
  canManageRegistrations: boolean;
  onCancelSuccess: () => void;
}

function formatDateTime(
  startDate: string | null,
  endDate: string | null,
  timezoneShortName: string | null,
  timezoneIdentifier: string | null,
): string {
  if (!startDate) return "Date TBD";

  const tz = timezoneIdentifier ?? undefined;
  const start = new Date(startDate);
  const dateStr = start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: tz,
  });
  const startTime = start.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: tz,
  });

  let result = `${dateStr} · ${startTime}`;

  if (endDate) {
    const end = new Date(endDate);
    const endTime = end.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    });
    result += ` - ${endTime}`;
  }

  if (timezoneShortName) {
    result += ` ${timezoneShortName}`;
  }

  return result;
}

function formatVenue(
  venueName: string | null,
  venueCity: string | null,
  venueState: string | null,
): string | null {
  if (!venueName && !venueCity) return null;

  const parts: string[] = [];
  if (venueName) parts.push(venueName);

  const location = [venueCity, venueState].filter(Boolean).join(", ");
  if (location) parts.push(location);

  return parts.join(" · ");
}

/**
 * Render a non-default status pill (Refunded / Cancelled). Active enrollees
 * show no badge — "active" is the default state and the pill was visual noise.
 */
function statusBadge(enrollee: MyRegistrationEnrollee) {
  if (enrollee.isRefunded) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
        Refunded
      </span>
    );
  }
  if (enrollee.isCancelled) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
        Cancelled
      </span>
    );
  }
  return null;
}

function isWithinCancellationWindow(startDate: string | null): boolean {
  if (!startDate) return false;
  const eventStart = new Date(startDate);
  const now = new Date();
  const fourteenDaysFromNow = new Date(
    now.getTime() + 14 * 24 * 60 * 60 * 1000,
  );
  return eventStart >= fourteenDaysFromNow;
}

function EnrolleeRow({
  enrollee,
  canManageRegistrations,
  onCancelSuccess,
  startDate,
}: {
  enrollee: MyRegistrationEnrollee;
  canManageRegistrations: boolean;
  onCancelSuccess: () => void;
  startDate: string | null;
}) {
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const withinWindow = isWithinCancellationWindow(startDate);
  const canCancel =
    canManageRegistrations && !enrollee.isCancelled && !enrollee.isRefunded;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axiosInstance.post(
        `/api/private/enrollments/${enrollee.enrollmentId}/cancel`,
        { reason },
      );
      toast({
        title: "Registration cancelled",
        description: `${enrollee.firstName} ${enrollee.lastName}'s registration has been cancelled.`,
      });
      setShowCancelForm(false);
      setReason("");
      onCancelSuccess();
    } catch (err: any) {
      toast({
        title: "Error",
        description:
          err?.response?.data?.message || "Failed to cancel registration.",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 py-2">
        <div className="min-w-0">
          <p className="text-sm text-gray-900 dark:text-white truncate">
            {enrollee.firstName} {enrollee.lastName}
            {enrollee.isOwnRegistration && (
              <span className="text-gray-400 dark:text-gray-500"> (you)</span>
            )}
          </p>
          {enrollee.email && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {enrollee.email}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {statusBadge(enrollee)}
        </div>
      </div>

      {canCancel && !showCancelForm && (
        <button
          className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          onClick={() => setShowCancelForm(true)}
          type="button"
        >
          Cancel registration
        </button>
      )}

      {showCancelForm && (
        <div className="pb-2 pl-0">
          {!withinWindow ? (
            <div className="p-3 rounded-md border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/20 mb-2">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Cancellations within 14 days of the event start date must be
                handled by the training team.
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                Please contact{" "}
                <span className="font-medium">training@mycertainpath.com</span>{" "}
                or call <span className="font-medium">945-270-8056</span>.
              </p>
              <div className="mt-2 flex justify-end">
                <Button
                  className="h-7 text-xs"
                  onClick={() => setShowCancelForm(false)}
                  size="sm"
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Textarea
                className="mb-2"
                maxLength={255}
                onChange={(e) => setReason(e.target.value.slice(0, 255))}
                placeholder="Reason for cancellation..."
                rows={2}
                value={reason}
              />
              <div className="flex justify-end gap-2">
                <Button
                  className="h-7 text-xs"
                  disabled={cancelling}
                  onClick={() => {
                    setShowCancelForm(false);
                    setReason("");
                  }}
                  size="sm"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  className="h-7 text-xs"
                  disabled={cancelling}
                  onClick={handleCancel}
                  size="sm"
                >
                  {cancelling && (
                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  )}
                  Confirm
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const MyRegistrationCard: React.FC<MyRegistrationCardProps> = ({
  registration,
  canManageRegistrations,
  onCancelSuccess,
}) => {
  const {
    eventUuid,
    eventThumbnailUrl,
    eventName,
    startDate,
    endDate,
    timezoneShortName,
    timezoneIdentifier,
    venueName,
    venueCity,
    venueState,
    confirmationNumber,
    isSessionCancelled,
    enrollments,
  } = registration;

  const dateTime = formatDateTime(
    startDate,
    endDate,
    timezoneShortName,
    timezoneIdentifier,
  );
  const venue = formatVenue(venueName, venueCity, venueState);

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
      <div className="flex gap-4">
        {/* Thumbnail */}
        <div className="flex-shrink-0">
          {eventThumbnailUrl ? (
            <img
              alt={eventName}
              className="w-20 h-20 rounded-lg object-cover"
              src={eventThumbnailUrl}
            />
          ) : (
            <div className="w-20 h-20 rounded-lg bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
              <CalendarDays className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-base font-normal text-gray-900 dark:text-white truncate">
              {eventName}
            </h3>
            {isSessionCancelled && (
              <span className="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                Session Cancelled
              </span>
            )}
          </div>

          <div className="mt-2 space-y-0.5">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {dateTime}
            </p>
            {venue && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {venue}
              </p>
            )}
            {confirmationNumber && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Confirmation: {confirmationNumber}
              </p>
            )}
          </div>
        </div>
      </div>

      <Separator className="my-3" />

      <div className="divide-y divide-gray-100 dark:divide-slate-700/50">
        {enrollments.map((enrollee) => (
          <EnrolleeRow
            canManageRegistrations={canManageRegistrations}
            enrollee={enrollee}
            key={enrollee.enrollmentId}
            onCancelSuccess={onCancelSuccess}
            startDate={startDate}
          />
        ))}
      </div>

      {/* View Event — bottom-right of the entire card */}
      {eventUuid && (
        <div className="mt-3 flex justify-end">
          <Button asChild size="sm" variant="outline">
            <Link to={`/event-registration/events/${eventUuid}`}>
              View Event
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default MyRegistrationCard;
